/**
 * @file Authentication related functions for Supabase
 */

import { supabase } from "@/integrations/supabase/client";
import type { AuthChangeEvent, Session } from "@supabase/supabase-js";
import { User } from "./types";
import { AuthError as SupabaseAuthError } from "./errors";
import { ErrorType, handleApiError } from "@/lib/errors";

/**
 * Signs up a new user with email and password
 * @param email - The user's email address
 * @param password - The user's chosen password
 * @returns Promise containing the signup response from Supabase
 * @throws {SupabaseAuthError} If signup fails
 */
export async function signUp(email: string, password: string) {
  try {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) {
      throw new SupabaseAuthError(error.message, { email });
    }
    return data;
  } catch (error) {
    // We use custom error handling for auth to preserve the SupabaseAuthError type
    // that our components expect
    if (error instanceof SupabaseAuthError) {
      throw error;
    }
    
    // For other errors, use our standard error handling
    handleApiError(error, 'Failed to sign up. Please try again.');
    throw error;
  }
}

/**
 * Signs in an existing user with email and password
 * @param email - The user's email address
 * @param password - The user's password
 * @returns Promise containing the signin response from Supabase
 * @throws {SupabaseAuthError} If signin fails
 */
export async function signIn(email: string, password: string) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      throw new SupabaseAuthError(error.message, { email });
    }
    return data;
  } catch (error) {
    // We use custom error handling for auth to preserve the SupabaseAuthError type
    // that our components expect
    if (error instanceof SupabaseAuthError) {
      throw error;
    }
    
    // For other errors, use our standard error handling
    handleApiError(error, 'Failed to sign in. Please try again.');
    throw error;
  }
}

/**
 * Signs out the current user
 * @returns Promise containing the signout response from Supabase
 */
export async function signOut() {
  try {
    return await supabase.auth.signOut();
  } catch (error) {
    handleApiError(error, 'Failed to sign out. Please try again.');
    throw error;
  }
}

/**
 * Retrieves the current session if it exists
 * @returns Promise containing the current session from Supabase
 */
export async function getSession() {
  try {
    return await supabase.auth.getSession();
  } catch (error) {
    handleApiError(error, 'Failed to get session information.');
    throw error;
  }
}

/**
 * Sets up a listener for authentication state changes
 * @param callback - Function to be called when auth state changes
 * @returns Object containing the subscription that can be unsubscribed
 */
export function onAuthStateChange(
  callback: (event: AuthChangeEvent, session: Session | null) => void
) {
  return supabase.auth.onAuthStateChange(callback);
}